import { MessageBar, MessageBarType, Spinner } from "@fluentui/react";
import { useEffect, useState } from "react";
import { ISensor } from "../../Models/ISensor";
import { ISentinelRich } from "../../Models/ISentinel";
import {
  ITestAlarmInfo,
  ITestAlarmRecipient,
  ITestAlarmResult,
  ITestAlarmSensor,
} from "../../Models/ITestAlarm";
import { IAPIResponse } from "../../Services/AjaxService";
import ApiService from "../../Services/ApiService";
import { getSensorIconTooltip } from "../../Utils/SensorMaps";
import { devLabel, dispName, sensorGivesAlarms } from "../../Pages/SetupPage/SetupHelpers";
import SmartModal, { DismissModal } from "../SmartModal/SmartModal";
import "./TestAlarmDialog.scss";

// Finestra "Prova allarme" di Setup > Dispositivo, in un solo passaggio:
// avviso (cosa succede e chi riceverà l'email), scelta del sensore, invio, esito dalla risposta del server.

export const TEST_ALARM_MODAL_ID = "testAlarmModal";

// cosa fa scattare ogni sensore, in parole semplici
const SENSOR_DESCRIPTION: Record<string, string> = {
  lidarDx: "La persona prova a scendere dal lato destro del letto.",
  lidarSx: "La persona prova a scendere dal lato sinistro del letto.",
  pesogr: "Il LiDAR non vede più la persona nel letto.",
  pesogr2: "Misura il peso.",
  rumore: "Un rumore forte nella stanza.",
  door: "La porta del varco si apre.",
};

// il peso (pesogr) scatta sotto la soglia: con soglia 0 un allarme vero non scatta mai.
// pesogr2 (bilancia) misura soltanto e non dà allarmi.
export const isWeightThresholdZero = (sensor: ISensor | undefined) => {
  return !!sensor && sensor.type === "pesogr" && +sensor.threshold === 0;
};

const hhmm = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toTimeString().slice(0, 5);
};

const listNonReceivers = (list: ITestAlarmRecipient[]) => {
  return list
    .map((x) => x.identifier + (x.reason ? " (" + x.reason + ")" : ""))
    .join(", ");
};

type Phase = "loading" | "choice" | "sending" | "result";

interface IOutcome {
  kind: "ok" | "warn" | "error";
  result?: ITestAlarmResult;
  sensorType?: string;
  message?: string;
}

export interface ITestAlarmDialogProps {
  // cambia a ogni apertura: la finestra riparte da capo
  requestId: number;
  sentinel?: ISentinelRich;
  onOpenNotifications: () => void;
  // alla chiusura della finestra: chi l'ha aperta aggiorna lo stato del dispositivo
  onClosed?: (sentinelId: number) => void;
}

const TestAlarmDialog = (props: ITestAlarmDialogProps) => {
  const [phase, setPhase] = useState<Phase>("loading");
  const [info, setInfo] = useState<ITestAlarmInfo | undefined>();
  const [infoError, setInfoError] = useState<string | undefined>();
  const [loadedAt, setLoadedAt] = useState<number>(Date.now());
  const [now, setNow] = useState<number>(Date.now());
  const [choice, setChoice] = useState<number | undefined>();
  const [outcome, setOutcome] = useState<IOutcome | undefined>();

  const sentinelId = props.sentinel?.id;

  const loadInfo = () => {
    if (sentinelId === undefined) return;
    setPhase("loading");
    ApiService.TestAlarmController.GetInfo(
      sentinelId,
      (response: IAPIResponse) => {
        if (response.error === null && response.parsed) {
          setInfo(response.parsed);
          setInfoError(undefined);
        } else {
          setInfo(undefined);
          setInfoError(response.error ?? "errore sconosciuto");
        }
        setLoadedAt(Date.now());
        setNow(Date.now());
        setPhase("choice");
      }
    );
  };

  useEffect(() => {
    if (props.requestId > 0) {
      setChoice(undefined);
      setOutcome(undefined);
      setInfo(undefined);
      setInfoError(undefined);
      loadInfo();
    }
  }, [props.requestId]);

  const remainingSeconds = (x: ITestAlarmSensor) => {
    if (!x.blockedSeconds || x.blockedSeconds <= 0) return 0;
    return Math.max(
      0,
      Math.ceil(x.blockedSeconds - (now - loadedAt) / 1000)
    );
  };

  // conto alla rovescia del blocco di 60 s: a zero si richiede lo stato al server
  const hasCountdown =
    phase === "choice" &&
    (info?.sensors ?? []).some((x) => x.blockedSeconds > 0);
  useEffect(() => {
    if (!hasCountdown) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [hasCountdown, loadedAt]);

  useEffect(() => {
    if (!hasCountdown || !info) return;
    const stillBlocked = info.sensors.some(
      (x) => x.blockedSeconds > 0 && remainingSeconds(x) > 0
    );
    if (!stillBlocked) {
      loadInfo();
    }
  }, [now]);

  const sensorBlockReason = (x: ITestAlarmSensor) => {
    if (!sensorGivesAlarms(x.type)) {
      return "Questo sensore misura e basta: non dà allarmi.";
    }
    const left = remainingSeconds(x);
    if (left > 0) {
      return "Prova appena inviata: si può rifare tra " + left + " s.";
    }
    if (!x.canTest) {
      return x.reason ?? "Questo sensore non si può provare.";
    }
    if (info && !info.canTest) {
      return info.reason ?? "Il dispositivo non si può provare.";
    }
    return null;
  };

  // sensori da mostrare: quelli del server, più la bilancia (pesogr2) se il server non la elenca, grigia
  const shownSensors: ITestAlarmSensor[] = info
    ? [
        ...info.sensors,
        ...(props.sentinel?.sensors ?? [])
          .filter(
            (y: ISensor) =>
              !sensorGivesAlarms(y.type) &&
              !info.sensors.some((x) => x.sensorId === y.id)
          )
          .map((y: ISensor) => ({
            sensorId: y.id,
            type: y.type,
            enabled: y.enabled,
            canTest: false,
            reason: null,
            blockedSeconds: 0,
          })),
      ]
    : [];

  const choiceSensor = shownSensors.find((x) => x.sensorId === choice);
  const choiceBlocked = choiceSensor ? sensorBlockReason(choiceSensor) : null;

  // un solo sensore provabile: già selezionato
  useEffect(() => {
    if (phase !== "choice" || !info) return;
    const available = shownSensors.filter((x) => sensorBlockReason(x) === null);
    if (available.length === 1 && (choice === undefined || choiceBlocked !== null)) {
      setChoice(available[0].sensorId);
    }
  }, [info, phase, now]);

  // perché "Invia allarme di prova" è grigio
  const sendBlockReason = () => {
    if (!info) return null;
    if (!choiceSensor) return "Scegli prima quale allarme provare.";
    return choiceBlocked;
  };

  const send = () => {
    if (sentinelId === undefined || !choiceSensor || choiceBlocked) return;
    setPhase("sending");
    const sensorType = choiceSensor.type;
    ApiService.TestAlarmController.Send(
      sentinelId,
      choiceSensor.sensorId,
      (response: IAPIResponse) => {
        if (response.error === null && response.parsed) {
          const result: ITestAlarmResult = response.parsed;
          const warn = !result.mailConfigured || !(result.emailsQueued > 0);
          setOutcome({ kind: warn ? "warn" : "ok", result, sensorType });
          // la Home lo mostra subito, senza aspettare il giro successivo
          document.dispatchEvent(new Event("reload-all-alarms"));
        } else {
          const status = response.raw?.status;
          let message = response.error ?? "errore sconosciuto";
          if (status === 429 && response.parsed?.retryAfter) {
            message +=
              " (si può riprovare tra " + response.parsed.retryAfter + " s)";
          } else if (status === 403) {
            message = "non autorizzato: " + message;
          }
          setOutcome({ kind: "error", message, sensorType });
        }
        setPhase("result");
      }
    );
  };

  const close = () => {
    DismissModal(TEST_ALARM_MODAL_ID);
  };

  const patientName =
    info?.patient?.identifier ?? props.sentinel?.patient?.identifier ?? "-";
  // il nome è quello di «Nome e posizione»; accanto tipo e numero del dispositivo
  const title = props.sentinel
    ? "Prova allarme · " + dispName(props.sentinel) + " (" + devLabel(props.sentinel) + ")"
    : "Prova allarme";

  const receivers = (info?.recipients ?? []).filter((x) => x.receives);
  const nonReceivers = (info?.recipients ?? []).filter((x) => !x.receives);

  // avviso giallo in tre righe: cosa succede, chi riceve l'email, chi no e perché
  const renderWarning = () => {
    if (!info) return null;
    return (
      <MessageBar messageBarType={MessageBarType.warning} isMultiline>
        <div className="test-alarm-line">
          Parte un allarme vero segnato <b>PROVA</b> (anche nell'oggetto
          dell'email): comparirà sulla Home e resterà finché un operatore non
          lo gestisce.
        </div>
        <div className="test-alarm-line">
          Ricevono l'email:{" "}
          {receivers.length > 0 ? (
            <b>{receivers.map((x) => x.identifier).join(", ")}</b>
          ) : (
            <b>nessuno</b>
          )}
          {!info.mailConfigured && " (la posta non è configurata: resteranno \"Non inviata\")"}.
        </div>
        {nonReceivers.length > 0 && (
          <div className="test-alarm-line">
            Non la ricevono: {listNonReceivers(nonReceivers)}.
          </div>
        )}
      </MessageBar>
    );
  };

  const renderChoices = () => {
    if (!info) return null;
    const sending = phase === "sending";
    return (
      <div>
        <div className="test-alarm-question">Quale allarme vuoi provare?</div>
        {shownSensors.length === 0 && (
          <div className="test-alarm-line">
            Questo dispositivo non ha sensori.
          </div>
        )}
        {shownSensors.map((x: ITestAlarmSensor) => {
          const why = sensorBlockReason(x);
          const blocked = !!why || sending;
          const selected = choice === x.sensorId;
          const sensor = props.sentinel?.sensors.find(
            (y: ISensor) => y.id === x.sensorId
          );
          return (
            <button
              type="button"
              key={x.sensorId}
              className={"test-alarm-choice" + (selected ? " on" : "")}
              aria-disabled={blocked}
              aria-pressed={selected}
              title={why ?? (sending ? "Invio in corso" : "")}
              onClick={() => {
                if (!blocked) setChoice(x.sensorId);
              }}
            >
              <span className="test-alarm-radio" />
              <span>
                <span className="test-alarm-choice-title">
                  {getSensorIconTooltip(x.type)}
                </span>
                {SENSOR_DESCRIPTION[x.type] && (
                  <span className="test-alarm-choice-desc">
                    {SENSOR_DESCRIPTION[x.type]}
                  </span>
                )}
                {why && <span className="test-alarm-choice-why">{why}</span>}
                {isWeightThresholdZero(sensor) && (
                  <span className="test-alarm-choice-note">
                    La prova parte, ma con soglia 0 un allarme vero di questo
                    tipo non scatta mai.
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderOutcome = () => {
    if (!outcome) return null;
    if (outcome.kind === "error") {
      return (
        <MessageBar messageBarType={MessageBarType.error} isMultiline>
          <b>Il server non ha creato l'allarme</b> ({outcome.message}). Nessuna
          email è partita. Puoi riprovare.
        </MessageBar>
      );
    }
    const r = outcome.result as ITestAlarmResult;
    const n = r.emailsQueued ?? 0;
    let emails = "";
    if (!r.mailConfigured) {
      // con la posta spenta non parte nulla: recipients dice quante email sono rimaste "Non inviata"
      emails =
        "Posta non configurata: " +
        (r.recipients ?? 0) +
        " email registrate come \"Non inviata\".";
    } else if (n === 0) {
      emails =
        "Nessuna email in coda: " +
        (nonReceivers.length > 0
          ? listNonReceivers(nonReceivers)
          : "nessun destinatario") +
        ".";
    } else {
      emails =
        n +
        " email in coda" +
        (receivers.length === n
          ? " (" + receivers.map((x) => x.identifier).join(", ") + ")"
          : "") +
        ": partono entro 15 secondi.";
    }
    return (
      <MessageBar
        messageBarType={
          outcome.kind === "ok" ? MessageBarType.success : MessageBarType.warning
        }
        isMultiline
      >
        <b>Allarme di prova creato alle {hhmm(r.createdAt)}</b>: "
        {getSensorIconTooltip(outcome.sensorType ?? "")}"
        {info?.hasPerson ? " di " + patientName : ""}, segnato PROVA. {emails}
        <div className="test-alarm-line">
          L'esito degli invii è in Notifiche &gt; Ultimi invii.
        </div>
      </MessageBar>
    );
  };

  const renderContent = () => {
    if (phase === "loading") {
      return (
        <div className="test-alarm-body">
          <Spinner label="Verifica del dispositivo..." />
        </div>
      );
    }
    if (phase === "result") {
      return <div className="test-alarm-body">{renderOutcome()}</div>;
    }
    if (!info) {
      return (
        <div className="test-alarm-body">
          <MessageBar messageBarType={MessageBarType.error} isMultiline>
            Impossibile sapere se il dispositivo si può provare ({infoError}).
          </MessageBar>
        </div>
      );
    }
    return (
      <div className="test-alarm-body">
        {!info.canTest && (
          <MessageBar messageBarType={MessageBarType.error} isMultiline>
            {info.reason ?? "Il dispositivo non si può provare."}
          </MessageBar>
        )}
        {renderWarning()}
        {renderChoices()}
        {phase === "choice" && sendBlockReason() && (
          <div className="test-alarm-send-why">{sendBlockReason()}</div>
        )}
      </div>
    );
  };

  const getButtons = () => {
    if (phase === "result") {
      if (outcome?.kind === "error") {
        return [
          {
            text: "Riprova",
            onClick: () => {
              setOutcome(undefined);
              loadInfo();
            },
          },
          { text: "Chiudi", onClick: close },
        ];
      }
      return [{ text: "Chiudi", onClick: close }];
    }
    const sending = phase === "sending";
    return [
      { text: "Annulla", onClick: close, disabled: sending },
      {
        text: sending ? "Invio..." : "Invia allarme di prova",
        iconName: sending ? "Sync" : "Ringer",
        onClick: send,
        disabled:
          sending ||
          phase === "loading" ||
          !choiceSensor ||
          choiceBlocked !== null,
      },
    ];
  };

  return (
    <SmartModal
      modalUniqueId={TEST_ALARM_MODAL_ID}
      title={title}
      content={renderContent()}
      buttons={getButtons()}
      leftButtons={
        phase === "result" && outcome?.kind !== "error"
          ? [
              {
                text: "Apri Notifiche",
                iconName: "Mail",
                onClick: () => {
                  close();
                  props.onOpenNotifications();
                },
              },
            ]
          : []
      }
      onDismiss={() => {
        if (sentinelId !== undefined && props.onClosed) {
          props.onClosed(sentinelId);
        }
      }}
    />
  );
};

export default TestAlarmDialog;
